import React, { useState } from 'react';
import { useAuth } from '@common/auth/use-auth';
import { useNavigate } from 'react-router-dom';
import { Trans } from '@ui/i18n/trans';
import { TextField } from '@common/ui/forms/input-field/text-field/text-field';
import { Button } from '@common/ui/buttons/button';
import { Form } from '@ui/forms/form';
import { useForm, Controller } from 'react-hook-form';
import { message } from '@ui/i18n/message';
import { toast } from '@ui/toast/toast';
import { FileUploadIcon } from '@ui/icons/material/FileUpload';
import { FormSelect, Option } from '@ui/forms/select/select';
import { PrimitiveValue } from '@common/ui/forms/listbox/types';

interface ShowFormData {
  show_name: string;
  show_description: string;
  show_date: string;
  show_time: string;
  show_location: string;
  show_ticket_link: string;
  media_type: 'banner' | 'video' | '';
  show_banner: File | null;
  show_video: File | null;
}

const DragDropIcon = () => (
  <svg className="mx-auto w-[25px] h-[25px] text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
  </svg>
);

const mediaTypeOptions = [
  { value: 'banner', label: 'Banner' },
  { value: 'video', label: 'Video' },
];

export function PageAgendaShowsCreate() {
  const navigate = useNavigate();
  const {user} = useAuth();
  const [uploading, setUploading] = useState(false);
  const form = useForm<ShowFormData>({
    defaultValues: {
      show_name: '',
      show_description: '',
      show_date: '',
      show_time: '',
      show_location: '',
      show_ticket_link: '',
      media_type: '',
      show_banner: null,
      show_video: null,
    },
  });
  
  const handleSubmit = async (values: ShowFormData) => {
    try {
      setUploading(true);
      const formData = new FormData();
      
      // Log form values for debugging
      console.log('Form values:', values);
      
      // Add user ID
      formData.append('user_id', user?.id?.toString() || '1');
      
      // Add text fields
      Object.entries({
        show_name: values.show_name,
        show_description: values.show_description,
        show_date: values.show_date,
        show_time: values.show_time,
        show_location: values.show_location,
        show_ticket_link: values.show_ticket_link || ''
      }).forEach(([key, value]) => {
        formData.append(key, value);
      });
      
      // Add media files based on media type
      if (values.media_type === 'banner' && values.show_banner instanceof File) {
        formData.append('show_banner', values.show_banner);
      }
      if (values.media_type === 'video' && values.show_video instanceof File) {
        formData.append('show_video', values.show_video);
      }

      // Log FormData contents for debugging
      for (let pair of formData.entries()) {
        console.log(pair[0] + ': ' + pair[1]);
      }

      console.log('Enviando solicitação para criar show...');
      const response = await fetch('/api/agenda-shows.php', {
        method: 'POST',
        body: formData,
      });

      const responseText = await response.text();
      console.log('Raw server response:', responseText);

      let data;
      try {
        data = JSON.parse(responseText);
      } catch (e) {
        console.error('Failed to parse server response:', e);
        throw new Error('Invalid server response');
      }

      if (!response.ok) {
        throw new Error(data.message || 'Server error occurred');
      }

      toast.positive(message(data.message || 'Programação do show criada com sucesso'));
      navigate('/');
    } catch (error) {
      console.error('Error creating show:', error);
      toast.danger(message(error instanceof Error ? error.message : 'Algo deu errado'));
    } finally {
      setUploading(false);
    }
  };

  return (
    <Form 
      form={form} 
      onSubmit={handleSubmit}
      className="container mx-auto p-16 max-w-[1024px]"
    >
      <div className="mb-40">
        <h1 className="text-3xl mb-20">
          <Trans message="Criar Nova Agenda" />
        </h1>
        
        <div className="space-y-20">
          <TextField
            name="show_name"
            label={<Trans message="Nome Show" />}
            className="flex-auto"
            required
            onChange={(e) => form.setValue('show_name', e.target.value)}
          />
          
          <TextField
            name="show_description"
            label={<Trans message="Descrição" />}
            className="flex-auto"
            inputElementType="textarea"
            rows={4}
            required
            onChange={(e) => form.setValue('show_description', e.target.value)}
          />
          
          <TextField
            name="show_date"
            label={<Trans message="Data Show" />}
            className="flex-auto"
            type="date"
            required
            onChange={(e) => form.setValue('show_date', e.target.value)}
          />
          
          <TextField
            name="show_time"
            label={<Trans message="Hora Show" />}
            className="flex-auto"
            type="time"
            required
            onChange={(e) => form.setValue('show_time', e.target.value)}
          />
          
          <TextField
            name="show_location"
            label={<Trans message="Local" />}
            className="flex-auto"
            required
            onChange={(e) => form.setValue('show_location', e.target.value)}
          />
          
          <TextField
            name="show_ticket_link"
            label={<Trans message="Link para comprar ingresso ou informações" />}
            className="flex-auto"
            onChange={(e) => form.setValue('show_ticket_link', e.target.value)}
          />
          
          <Controller
            name="media_type"
            control={form.control}
            render={({ field }) => (
              <FormSelect
                name={field.name}
                label={<Trans message="Tipo de Media" />}
                className="w-full bg-gray-50"
                selectionMode="single"
                selectedValue={field.value}
                onSelectionChange={(value: PrimitiveValue) => field.onChange(value as string)}
              >
                {mediaTypeOptions.map((option) => (
                  <Option key={option.value} value={option.value}>
                    {option.label}
                  </Option>
                ))}
              </FormSelect>
            )}
          />
          
          {form.watch('media_type') === 'banner' && (
            <div className="mb-20">
              <label className="block text-sm font-medium mb-4">
                <Trans message="Banner Evendo" />
              </label>
              <Controller
                name="show_banner"
                control={form.control}
                render={({ field }) => (
                  <div
                    className="border-2 border-dashed rounded p-20 text-center cursor-pointer hover:border-primary"
                    onClick={() => {
                      const input = document.createElement('input');
                      input.type = 'file';
                      input.accept = 'image/*';
                      input.onchange = (e: any) => {
                        const file = e.target.files[0];
                        field.onChange(file);
                      };
                      input.click();
                    }}
                  >
                    {field.value ? (
                      <div>
                        <img
                          src={URL.createObjectURL(field.value)}
                          alt="Preview"
                          className="max-h-40 mx-auto mb-10"
                        />
                        <p className="text-sm">{field.value.name}</p>
                      </div>
                    ) : (
                      <>
                        <DragDropIcon />
                        <p className="mt-8 text-sm">
                          <Trans message="Clique para carregar a imagem do banner" />
                        </p>
                      </>
                    )}
                  </div>
                )}
              />
            </div>
          )}
          
          {form.watch('media_type') === 'video' && (
            <div className="mb-20">
              <label className="block text-sm font-medium mb-4">
                <Trans message="Video Evendo" />
              </label>
              <Controller
                name="show_video"
                control={form.control}
                render={({ field }) => (
                  <div
                    className="border-2 border-dashed rounded p-20 text-center cursor-pointer hover:border-primary"
                    onClick={() => {
                      const input = document.createElement('input');
                      input.type = 'file';
                      input.accept = 'video/*';
                      input.onchange = (e: any) => {
                        const file = e.target.files[0];
                        field.onChange(file);
                      };
                      input.click();
                    }}
                  >
                    {field.value ? (
                      <div>
                        <p className="text-sm">{field.value.name}</p>
                      </div>
                    ) : (
                      <>
                        <DragDropIcon />
                        <p className="mt-8 text-sm">
                          <Trans message="Clique para enviar o vídeo" />
                        </p>
                      </>
                    )}
                  </div>
                )}
              />
            </div>
          )}
          
          <Button
            type="submit"
            variant="flat"
            color="primary"
            size="md"
            disabled={uploading}
             className="mb-50"
          >
            <Trans message="Publicar Agenda" />
          </Button>
        </div>
      </div>
    </Form>
  );
}
